import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";

/**
 * 수신자 도움창. 좌:그룹(MAS_GP, grid1) / 우:그룹별 사용자(SYS_USER, grid2) / 하:수신자 바구니(grid3).
 * 그룹 추가 → {fg_gp:'G', cd_receiver:cd_gp}, 사용자 추가 → {fg_gp:'U', cd_receiver:cd_user}.
 * OK 시 바구니 반환: rtnData:[ [{fg_gp, cd_receiver, nm_receiver, gubun}] ]
 */
interface popupProps {
  modalRef?: any;
}

const SN_MAS_SET_REG_007_POP_01 = forwardRef(
  ({ modalRef }: popupProps, ref: React.Ref<any>) => {
    const groupRef = useRef<any>(null);
    const userRef = useRef<any>(null);

    const [groupCols, setGroupCols] = useState<any[]>([]);
    const [userCols, setUserCols] = useState<any[]>([]);
    const [basketCols, setBasketCols] = useState<any[]>([]);
    const [groups, setGroups] = useState<any[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [basket, setBasket] = useState<any[]>([]);
    // 전체 사용자 캐시 → 그룹 포커스 시 cd_gp 로 로컬 필터 (추가 조회 없음)
    const allUsers = useRef<any[]>([]);

    useEffect(() => {
      (async () => {
        try {
          const rtn: any = await getInitPopUpData("SN_MAS_SET_REG_007_POP_01", {});
          setGroupCols(rtn.grid?.grid1 ?? []);
          setUserCols(rtn.grid?.grid2 ?? []);
          setBasketCols(rtn.grid?.grid3 ?? []);
          // 다중결과셋: [0]그룹 [1]전체사용자
          const d: any = await fnGetDataSet({ id: "sn_mas_set_reg_007_pop_01", params: {} });
          setGroups(d?.[0] || []);
          allUsers.current = d?.[1] || [];
        } catch (e) {}
      })();
    }, []);

    const onGroupFocused = (row: any) => {
      if (!row?.cd_gp) return setUsers([]);
      setUsers(allUsers.current.filter((u) => u.cd_gp === row.cd_gp));
    };

    const addToBasket = (items: any[]) => {
      setBasket((prev) => {
        const key = (r: any) => `${r.fg_gp}|${r.cd_receiver}`;
        const seen = new Set(prev.map(key));
        return [...prev, ...items.filter((r) => !seen.has(key(r)))];
      });
    };
    const addGroups = () =>
      addToBasket(
        (groupRef.current?.getCheckedRows?.() ?? []).map((g: any) => ({
          fg_gp: "G",
          cd_receiver: g.cd_gp,
          nm_receiver: g.nm_gp,
          gubun: "그룹",
        }))
      );
    const addUsers = () =>
      addToBasket(
        (userRef.current?.getCheckedRows?.() ?? []).map((u: any) => ({
          fg_gp: "U",
          cd_receiver: u.cd_user,
          nm_receiver: u.nm_user,
          gubun: "유저",
        }))
      );
    const removeBasket = (r: any) =>
      setBasket((prev) =>
        prev.filter((b) => !(b.fg_gp === r.fg_gp && b.cd_receiver === r.cd_receiver))
      );

    useImperativeHandle(ref, () => ({
      checkValidity: async () => {
        if (!basket.length) {
          await showDialog("alter", "수신자를 선택하세요.");
          return false;
        }
        return true;
      },
      getOkayData: () => ({ rtnData: [basket] }),
    }));

    const popupParent = modalRef?.current?.getPopupParent?.();

    return (
      <div className="h-full flex flex-col p-1 gap-1">
        <div className="flex flex-1 min-h-0 gap-1">
          <div className="w-1/3 flex flex-col min-h-0">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold" style={{ color: "rgb(11,12,105)" }}>그룹</span>
              <SnButton text={"추가"} onClick={addGroups} />
            </div>
            <div className="flex-1 min-h-0">
              <SnGrid
                ref={groupRef}
                columnConfig={groupCols}
                rowData={groups}
                etcProps={{
                  ischeckboxSelection: true,
                  rowSelection: "single",
                  isEditable: false,
                  title: "",
                  popupParent,
                }}
                events={{ onFocusedRowChanged: onGroupFocused }}
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold" style={{ color: "rgb(11,12,105)" }}>사용자</span>
              <SnButton text={"추가"} onClick={addUsers} />
            </div>
            <div className="flex-1 min-h-0">
              <SnGrid
                ref={userRef}
                columnConfig={userCols}
                rowData={users}
                etcProps={{
                  ischeckboxSelection: true,
                  rowSelection: "multiple",
                  isEditable: false,
                  title: "",
                  popupParent,
                }}
                events={{}}
              />
            </div>
          </div>
        </div>
        <div style={{ height: 180 }} className="flex flex-col">
          <span className="text-sm font-bold" style={{ color: "rgb(11,12,105)" }}>수신자</span>
          <div className="flex-1 min-h-0">
            <SnGrid
              columnConfig={basketCols}
              rowData={basket}
              etcProps={{ rowSelection: "single", isEditable: false, title: "", popupParent }}
              events={{ onCellDoubleClicked: (e: any) => e?.rowData && removeBasket(e.rowData) }}
            />
          </div>
          <div className="text-xs text-gray-400">행 더블클릭 시 수신자에서 제거</div>
        </div>
      </div>
    );
  }
);

export default SN_MAS_SET_REG_007_POP_01;
